import {
  Children,
  isValidElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { ShowMoreButton } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useExpandablePagination } from "../useExpandablePagination";

interface PaginatedExpandableListProps<T> {
  isOpen: boolean;
  children: ReactNode;
  className?: string;
  groupId: string;
  items: T[];
}

/**
 * An expandable list with pagination and smooth height animation
 */
const PaginatedExpandableList = <T,>({
  isOpen,
  children,
  className,
  items,
  groupId,
}: PaginatedExpandableListProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  const { showMore, showLess, hasMoreToShow, canShowLess, visibleCount } =
    useExpandablePagination(items, groupId);

  const childArray = Children.toArray(children).filter(isValidElement);
  const slicedChildren = childArray.slice(0, visibleCount);

  useEffect(() => {
    if (ref.current && isOpen) {
      // Use requestAnimationFrame to ensure we get the correct height after DOM updates
      requestAnimationFrame(() => {
        setHeight(ref.current?.scrollHeight ?? 0);
      });
    } else {
      setHeight(0);
    }
  }, [isOpen, visibleCount, items.length]); // Add items.length as a dependency

  return (
    <div
      className={cn(
        "transition-[height,opacity] duration-300 ease-in-out overflow-hidden",
        isOpen ? "opacity-100" : "opacity-0",
        className,
      )}
      style={{ height }}
    >
      <div ref={ref}>
        {slicedChildren}
        {hasMoreToShow && (
          <ShowMoreButton isExpanded={false} onToggle={showMore} />
        )}
        {canShowLess && !hasMoreToShow && (
          <ShowMoreButton isExpanded={true} onToggle={showLess} />
        )}
      </div>
    </div>
  );
};

export default PaginatedExpandableList;
